import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CreateSaasProductDto,
  QuerySaasProductsDto,
  UpdateSaasProductDto,
} from '@control-center/shared';

import {
  createSaasProduct,
  deleteSaasProduct,
  fetchSaasProduct,
  fetchSaasProducts,
  updateSaasProduct,
  type SaasProductEntity,
} from '../lib/saas-products-api';

export const saasProductsKeys = {
  all: ['saas-products'] as const,
  lists: () => [...saasProductsKeys.all, 'list'] as const,
  list: (filters: QuerySaasProductsDto) => [...saasProductsKeys.lists(), filters] as const,
  detail: (id: string) => [...saasProductsKeys.all, 'detail', id] as const,
};

export function useSaasProducts(filters: QuerySaasProductsDto = { limit: 50 }) {
  return useQuery({
    queryKey: saasProductsKeys.list(filters),
    queryFn: () => fetchSaasProducts(filters),
    staleTime: 30_000,
  });
}

export function useSaasProduct(id: string | undefined) {
  return useQuery<SaasProductEntity>({
    queryKey: saasProductsKeys.detail(id ?? ''),
    queryFn: () => fetchSaasProduct(id as string),
    enabled: Boolean(id),
    staleTime: 30_000,
  });
}

export function useCreateSaasProduct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateSaasProductDto) => createSaasProduct(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: saasProductsKeys.all });
    },
  });
}

export function useUpdateSaasProduct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateSaasProductDto }) =>
      updateSaasProduct(id, payload),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: saasProductsKeys.all });
      qc.invalidateQueries({ queryKey: saasProductsKeys.detail(vars.id) });
    },
  });
}

export function useDeleteSaasProduct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, version }: { id: string; version: number }) =>
      deleteSaasProduct(id, version),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: saasProductsKeys.all });
    },
  });
}
